import {FluxAvatar} from "@buildwithflux/avatar-react-mui";
import type {FollowAccountType} from "@buildwithflux/following-schema";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Paper from "@mui/material/Paper";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import {useCallback, useState} from "react";
import {Link as RouterLink} from "react-router";

import {FollowerCounter, ProjectCounter} from "./Counter";
import FollowButton from "./FollowButton";

export interface AccountHit {
    uid: string;
    handle: string;
    displayName: string;
    accountType: FollowAccountType;
    followerCount?: number;
    projectCount?: number;
    verified?: boolean;
    /** Display-ready avatar URL resolved from the search hit's picture projection. */
    pictureUrl?: string;
}

const AVATAR_SIZE = 40;

export default function AccountCard({account}: {account: AccountHit}) {
    const profileUrl = `/${account.handle}`;

    // Mirror production: the follower counter optimistically tracks the viewer's
    // own follow/unfollow action (+1 / -1), reverting if the write ultimately fails.
    const [followerCount, setFollowerCount] = useState(account.followerCount);
    const onFollowChange = useCallback((following: boolean) => {
        setFollowerCount((prev) => (prev === undefined ? prev : prev + (following ? 1 : -1)));
    }, []);

    return (
        <>
            <Paper square component="li" sx={{p: 2}}>
                <Box sx={{display: "flex", flexWrap: "wrap", justifyContent: "flex-start", alignItems: "center"}}>
                    <Box sx={{pr: "16px", flexGrow: 0.5}}>
                        <Tooltip title={account.handle} placement="bottom">
                            <Link component={RouterLink} to={profileUrl} underline="none">
                                <FluxAvatar
                                    uid={account.uid}
                                    size={AVATAR_SIZE}
                                    alt={account.handle}
                                    pictureUrl={account.pictureUrl}
                                    displayName={account.displayName}
                                />
                            </Link>
                        </Tooltip>
                    </Box>

                    <Box sx={{width: "360px", flexGrow: 11}}>
                        <Link component={RouterLink} to={profileUrl} color="text.primary" underline="none">
                            <Typography color="text.primary" variant="body1" component="h3" sx={{mb: 0}}>
                                {account.displayName}
                            </Typography>
                        </Link>

                        <Box sx={{display: "flex", alignItems: "center", minWidth: 0}}>
                            <Tooltip title={account.handle} placement="top">
                                <Link
                                    component={RouterLink}
                                    to={profileUrl}
                                    color="primary"
                                    underline="hover"
                                    variant="body2"
                                    noWrap
                                    sx={{
                                        display: "block",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                        minWidth: 0,
                                    }}
                                >
                                    {account.handle}
                                </Link>
                            </Tooltip>
                            {account.verified ? (
                                <CheckCircleIcon
                                    sx={{
                                        fontSize: 12,
                                        ml: "2px",
                                        flexShrink: 0,
                                        color: "primary.main",
                                    }}
                                />
                            ) : null}
                        </Box>

                        <Box sx={{mt: "2px", display: "flex", gap: 1.25}}>
                            <FollowerCounter count={followerCount} />
                            <ProjectCounter count={account.projectCount} />
                        </Box>
                    </Box>

                    <Box sx={{flexGrow: 2}}>
                        <FollowButton
                            accountUid={account.uid}
                            accountType={account.accountType}
                            onFollowChange={onFollowChange}
                        />
                    </Box>
                </Box>
            </Paper>
            <Divider />
        </>
    );
}
